import React, { ComponentPropsWithoutRef, ReactNode, useState } from 'react';
// import ReachAlert from '@reach/alert';
import {
  ActionsCancelCircleHighlightedMedium as ErrorIcon,
  ActionsCheckCircleOnMedium as SuccessIcon,
  NavigationWarningHighlightedMedium as WarningIcon,
  NavigationInformationNormalMedium as InfoIcon,
  ActionsCancelNormalSmall as CancelIcon
} from '@strava/icons';
import { useInterval } from '@strava/react-hooks';
import { isTest } from '@strava/utils/src/dev.utils';
import clsx from '../clsx';
import VisuallyHidden from '../VisuallyHidden';
import styles from './Alert.module.scss';

export const AlertStatus = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info'
} as const;

export type AlertStatusType = (typeof AlertStatus)[keyof typeof AlertStatus];

export type AlertStyleType =
  | 'hasError'
  | 'hasSuccess'
  | 'hasWarning'
  | 'hasInfo';
export type AlertButtonStyleType =
  | 'buttonError'
  | 'buttonSuccess'
  | 'buttonWarning'
  | 'buttonInfo';

const ALERT_DELAY = 8000;

const getAlertDetails = ({
  type,
  iconSize
}: {
  type: AlertStatusType;
  iconSize: number;
}): {
  indicatorIcon: ReactNode;
  messageType: AlertStatusType;
  alertStyle: AlertStyleType;
  buttonStyle: AlertButtonStyleType;
} =>
  ({
    [AlertStatus.ERROR]: {
      indicatorIcon: (
        <ErrorIcon
          aria-hidden={true}
          data-testid="error-icon"
          size={iconSize}
        />
      ),
      messageType: AlertStatus.ERROR,
      alertStyle: 'hasError' as const,
      buttonStyle: 'buttonError' as const
    },
    [AlertStatus.SUCCESS]: {
      indicatorIcon: (
        <SuccessIcon
          aria-hidden={true}
          data-testid="success-icon"
          size={iconSize}
        />
      ),
      messageType: AlertStatus.SUCCESS,
      alertStyle: 'hasSuccess' as const,
      buttonStyle: 'buttonSuccess' as const
    },
    [AlertStatus.WARNING]: {
      indicatorIcon: (
        <WarningIcon
          aria-hidden={true}
          data-testid="warning-icon"
          size={iconSize}
        />
      ),
      messageType: AlertStatus.WARNING,
      alertStyle: 'hasWarning' as const,
      buttonStyle: 'buttonWarning' as const
    },
    [AlertStatus.INFO]: {
      indicatorIcon: (
        <InfoIcon aria-hidden={true} data-testid="info-icon" size={iconSize} />
      ),
      messageType: AlertStatus.INFO,
      alertStyle: 'hasInfo' as const,
      buttonStyle: 'buttonInfo' as const
    }
  }[type]);

const fadeStyles = {
  enter: isTest() ? '' : 'fadeIn',
  exit: isTest() ? '' : 'fadeOut'
};

export interface AlertProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  alertType?: AlertStatusType;
  autoDismiss?: boolean;
  autoDismissDelay?: number;
  className?: string;
  contentPosition?: 'left' | 'center' | 'right';
  hideCloseButton?: boolean;
  hideIcon?: boolean;
  iconSize?: number;
  onClose?: () => void;
}

export const Alert = ({
  children,
  className,
  alertType = AlertStatus.ERROR,
  autoDismiss = false,
  autoDismissDelay = ALERT_DELAY,
  contentPosition = 'left',
  hideCloseButton = false,
  hideIcon = true,
  iconSize = 32,
  onClose = () => {},
  ...options
}: AlertProps) => {
  const { indicatorIcon, messageType, alertStyle, buttonStyle } =
    getAlertDetails({ type: alertType, iconSize });
  const [fadeStyle, setFadeStyle] = useState(fadeStyles.enter);

  const handleOnClose = async () => {
    setFadeStyle(fadeStyles.exit);
    // eslint-disable-next-line no-unused-expressions
    !isTest() && (await new Promise((resolve) => setTimeout(resolve, 0)));
    onClose();
  };

  useInterval(
    () => {
      handleOnClose();
    },
    autoDismiss ? autoDismissDelay : null
  );

  return (
    <div
      className={clsx(
        'text-subhead',
        styles.alert,
        styles[alertStyle],
        className,
        {
          [styles.fadeIn as string]: fadeStyle === fadeStyles.enter,
          [styles.fadeOut as string]: fadeStyle === fadeStyles.exit
        }
      )}
      data-testid={`alert-${options.id}`}
      role="alert"
      aria-live="polite"
      {...options}
    >
      <div className={styles.alertInner}>
        {/* Icon */}
        {!hideIcon && (
          <div className={styles.indicatorIcon}>{indicatorIcon}</div>
        )}

        {/* Content */}
        <div
          className={clsx(styles.alertContent, {
            [styles.alignLeft as string]: contentPosition === 'left',
            [styles.alignCenter as string]: contentPosition === 'center',
            [styles.alignRight as string]: contentPosition === 'right'
          })}
        >
          <VisuallyHidden>{messageType}: </VisuallyHidden>
          <div>{children}</div>
        </div>

        {/* Close Button */}
        {!hideCloseButton && (
          <div className={styles.closeButtonContainer}>
            <button
              onClick={handleOnClose}
              aria-label="Close alert"
              className={clsx(styles.button, styles[buttonStyle])}
            >
              <CancelIcon aria-hidden={true} />
            </button>
          </div>
        )}
      </div>

      {/* Auto Dismiss Progress */}
      {autoDismiss && (
        <div
          className={styles.autoDismissBar}
          data-style="smooth"
          // @ts-ignore - this is a valid style property
          style={{ '--duration': autoDismissDelay }}
          data-testid="alert-auto-dismiss"
        >
          <div className={styles[alertStyle]} />
        </div>
      )}
    </div>
  );
};

export default Alert;
